import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CompleteIcon,
  Construction as BuildIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Settings as InProgressIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { PageHeader, Loading, EmptyState } from '../components/Layout';
import api from '../services/api';
import tenantService from '../services/tenantService';
import roomService from '../services/roomService';

const Maintenance = () => {
  const [requests, setRequests] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [formData, setFormData] = useState({
    tenant_id: '',
    room_id: '',
    description: '',
    priority: 'medium',
    category: 'general',
    status: 'pending',
    assigned_to: '',
    estimated_cost: '',
    notes: '',
  });

  useEffect(() => {
    fetchRequests();
    fetchTenants();
    fetchRooms();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/maintenance');
      setRequests(response.data || []);
    } catch (error) {
      console.error('Failed to load maintenance requests:', error);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await tenantService.getAllTenants();
      setTenants(response.data.filter(t => t.is_active));
    } catch (error) {
      console.error('Failed to load tenants:', error);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await roomService.getAllRooms();
      setRooms(response.data);
    } catch (error) {
      console.error('Failed to load rooms:', error);
    }
  };

  const handleOpenDialog = (request = null) => {
    if (request) {
      setEditingRequest(request);
      setFormData({
        tenant_id: request.tenant_id || '',
        room_id: request.room_id || '',
        description: request.description || '',
        priority: request.priority || 'medium',
        category: request.category || 'general',
        status: request.status || 'pending',
        assigned_to: request.assigned_to || '',
        estimated_cost: request.estimated_cost || '',
        notes: request.notes || '',
      });
    } else {
      setEditingRequest(null);
      setFormData({
        tenant_id: '',
        room_id: '',
        description: '',
        priority: 'medium',
        category: 'general',
        status: 'pending',
        assigned_to: '',
        estimated_cost: '',
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRequest(null);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    if (!formData.description || !formData.room_id) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const data = {
        ...formData,
        estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null,
      };

      if (editingRequest) {
        await api.put(`/api/maintenance/${editingRequest.id}`, data);
        toast.success('Maintenance request updated successfully');
      } else {
        await api.post('/api/maintenance', data);
        toast.success('Maintenance request created successfully');
      }
      handleCloseDialog();
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this maintenance request?')) {
      try {
        await api.delete(`/api/maintenance/${id}`);
        toast.success('Maintenance request deleted successfully');
        fetchRequests();
      } catch (error) {
        toast.error('Failed to delete maintenance request');
        console.error(error);
      }
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await api.patch(`/api/maintenance/${id}/status`, { status });
      toast.success(`Status updated to ${status}`);
      fetchRequests();
    } catch (error) {
      toast.error('Failed to update status');
      console.error(error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'info';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CompleteIcon fontSize="small" />;
      case 'in_progress':
        return <InProgressIcon fontSize="small" />;
      case 'pending':
        return <PendingIcon fontSize="small" />;
      case 'cancelled':
        return <ErrorIcon fontSize="small" />;
      default:
        return <BuildIcon fontSize="small" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const filteredRequests = requests.filter(r => {
    if (tabValue === 0) return true; // All
    if (tabValue === 1) return r.status === 'pending';
    if (tabValue === 2) return r.status === 'in_progress';
    if (tabValue === 3) return r.status === 'completed';
    return true;
  });

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    inProgress: requests.filter(r => r.status === 'in_progress').length,
    completed: requests.filter(r => r.status === 'completed').length,
  };

  if (loading) {
    return <Loading message="Loading maintenance requests..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Maintenance Requests"
        subtitle="Manage tenant maintenance and repair requests"
        breadcrumbs={[{ label: 'Maintenance' }]}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            New Request
          </Button>
        }
      />

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Requests
              </Typography>
              <Typography variant="h4">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                In Progress
              </Typography>
              <Typography variant="h4" color="info.main">
                {stats.inProgress}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completed
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.completed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="All" />
        <Tab label="Pending" />
        <Tab label="In Progress" />
        <Tab label="Completed" />
      </Tabs>

      {/* Requests Table */}
      <Paper sx={{ p: 3 }}>
        {filteredRequests.length === 0 ? (
          <EmptyState
            icon={<BuildIcon sx={{ fontSize: 60 }} />}
            title="No Maintenance Requests"
            description="No maintenance requests found for the selected filter"
          />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Room</TableCell>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Cost</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{formatDate(request.created_at)}</TableCell>
                    <TableCell>
                      {rooms.find(r => r.id === request.room_id)?.room_number || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {tenants.find(t => t.id === request.tenant_id)?.full_name || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {request.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={request.category} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.priority}
                        color={getPriorityColor(request.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(request.status)}
                        label={request.status}
                        color={getStatusColor(request.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {request.estimated_cost ? `KES ${request.estimated_cost.toLocaleString()}` : 'N/A'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(request)}
                        title="Edit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {request.status === 'pending' && (
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleUpdateStatus(request.id, 'in_progress')}
                          title="Start"
                        >
                          <InProgressIcon fontSize="small" />
                        </IconButton>
                      )}
                      {request.status === 'in_progress' && (
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleUpdateStatus(request.id, 'completed')}
                          title="Complete"
                        >
                          <CompleteIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(request.id)}
                        title="Delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRequest ? 'Edit Maintenance Request' : 'New Maintenance Request'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Room *"
                  name="room_id"
                  value={formData.room_id}
                  onChange={handleInputChange}
                  required
                >
                  <MenuItem value="">-- Select Room --</MenuItem>
                  {rooms.map((room) => (
                    <MenuItem key={room.id} value={room.id}>
                      {room.room_number} - {room.property_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Tenant"
                  name="tenant_id"
                  value={formData.tenant_id}
                  onChange={handleInputChange}
                >
                  <MenuItem value="">-- Select Tenant --</MenuItem>
                  {tenants.map((tenant) => (
                    <MenuItem key={tenant.id} value={tenant.id}>
                      {tenant.full_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description *"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                >
                  <MenuItem value="general">General</MenuItem>
                  <MenuItem value="plumbing">Plumbing</MenuItem>
                  <MenuItem value="electrical">Electrical</MenuItem>
                  <MenuItem value="carpentry">Carpentry</MenuItem>
                  <MenuItem value="painting">Painting</MenuItem>
                  <MenuItem value="hvac">HVAC</MenuItem>
                  <MenuItem value="appliance">Appliance</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Estimated Cost (KES)"
                  name="estimated_cost"
                  type="number"
                  value={formData.estimated_cost}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Assigned To"
                  name="assigned_to"
                  value={formData.assigned_to}
                  onChange={handleInputChange}
                  placeholder="Contractor name"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editingRequest ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Maintenance;
